import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { TradingPlatformServers } from '@monorepo/client-common';
import { getTradingSystemName } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import { Box, Stack, Typography } from '@monorepo/nova-ui';
import { UnavailableValue } from '@monorepo/types';
import { CredentialsSectionProps } from '../types';
import { InfoRow } from './info-row';
import { PlatformButtonsSection } from './platform-buttons-section';

export const CredentialsSection: FunctionComponent<CredentialsSectionProps> = ({
  tsUser,
  tradingSystem,
  showPassword,
  togglePasswordVisibility,
  handleCopy,
  credentialsTitleText,
  credentialsSectionStyles,
  isPrimaryColor,
  platformButtonsTitleText,
  direction = 'row',
  loading,
}) => {
  const { t } = useTranslation(['login', 'programs']);
  const tradingServer = TradingPlatformServers[tradingSystem];
  const tradingPlatformName = t(
    `programs:trading_system.${tradingSystem}_hub`,
    { defaultValue: getTradingSystemName(tradingSystem) },
  );

  return (
    <Stack gap={3} id="credentials-section-stack">
      {credentialsTitleText && (
        <Typography variant="body2" fontWeight={400}>
          {credentialsTitleText} {loading ? '' : `${tradingPlatformName}:`}
        </Typography>
      )}
      <Box sx={credentialsSectionStyles} id="credentials-section-box">
        {loading ? (
          <SystemIcon iconName="HourglassHigh" size="large" />
        ) : (
          <Stack gap={2} flexWrap="wrap" id="credentials-section" width="100%">
            <InfoRow
              label={t('login:traders_login.login')}
              value={tsUser.login || UnavailableValue.Dash}
              copyable={true}
              onCopy={handleCopy}
            />
            <InfoRow
              label={t('login:traders_login.password')}
              value={tsUser.password || UnavailableValue.Dash}
              copyable={true}
              isPassword={true}
              showPassword={showPassword}
              onTogglePassword={togglePasswordVisibility}
              onCopy={handleCopy}
              tsUser={tsUser}
            />
            <InfoRow
              label={t('login:traders_login.platform')}
              value={tradingPlatformName}
              copyable={true}
              onCopy={handleCopy}
            />
            {tradingServer && (
              <InfoRow
                label={t('login:traders_login.server')}
                value={tradingServer}
                copyable={true}
                onCopy={handleCopy}
              />
            )}
          </Stack>
        )}
      </Box>
      {platformButtonsTitleText && (
        <Typography variant="body2" fontWeight={400}>
          {platformButtonsTitleText}
        </Typography>
      )}
      <PlatformButtonsSection
        tradingSystem={tradingSystem}
        direction={direction}
        isPrimaryColor={isPrimaryColor ?? false}
      />
    </Stack>
  );
};
